import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Form, Grid, GridColumn, GridRow } from "semantic-ui-react";

import { DATE_FORMATS_FNS, FileDropdown } from "components";
import { InitialLogValues, LogUpdates } from "components/SnoozeModal/components";
import "components/SnoozeModal/components/LogHistory/LogHistory.scss";
import { useSnoozeDepartmentOptions, useSnoozeTypeOptions } from "components/SnoozeModal/hooks";
import { getSnoozeStatusOptions } from "components/SnoozeModal/util";
import { SNOOZE_STATUSES } from "models";
import { getAllSnoozeAttachments } from "modules/AppointmentDetails/utils";
import { formatDate } from "utils/dateHelperFunctions";

import type { Dealer, Snooze } from "models";

type LogHistoryProps = {
  snoozeHistory?: Snooze[];
  dealer?: Dealer | null;
};

export const LogHistory = ({ snoozeHistory = [], dealer }: LogHistoryProps) => {
  const { t } = useTranslation();

  const { snoozeTypeOptions } = useSnoozeTypeOptions(dealer);
  const { snoozeStatusOptions } = getSnoozeStatusOptions(t);
  const { snoozeDepartmentOptions } = useSnoozeDepartmentOptions();

  const attachments = useMemo(() => {
    return getAllSnoozeAttachments(snoozeHistory);
  }, [snoozeHistory]);

  return (
    <Form.Group className="LogHistory-Container">
      <Form.Field width={16}>
        <div className="log-history-heading-container">
          <span className="log-history-title-container">{t("v8_log_history", "Log History")}</span>
          {attachments.length > 0 && (
            <div className="log-history-attachments-container">
              <FileDropdown files={attachments} iconColor="#000000" />
            </div>
          )}
        </div>
        <div className={`LogHistory ${snoozeHistory.length === 0 ? "empty" : ""} log-history-container`}>
          {snoozeHistory.length ? (
            snoozeHistory.map((snoozeItem, i) => (
              <div className="inner-row" key={snoozeItem.id}>
                <Grid>
                  <GridRow>
                    <GridColumn width={12}>
                      <GridRow>
                        <strong>
                          {snoozeItem.user?.first_name} {snoozeItem.user?.last_name}
                        </strong>
                        {" - "}
                        {formatDate(snoozeItem.created_on, DATE_FORMATS_FNS.timeDateMonthYear)}
                        {snoozeItem.snooze_status_id === SNOOZE_STATUSES.Deleted && (
                          <>
                            {" - "}
                            {<strong style={{ color: "red" }}>{t("v8_deleted", "Deleted")}</strong>}
                          </>
                        )}
                      </GridRow>
                      {i === snoozeHistory.length - 1 || snoozeHistory[i - 1]?.snooze_status_id === SNOOZE_STATUSES.Deleted ? (
                        <InitialLogValues
                          snoozeItem={snoozeItem}
                          snoozeTypeOptions={snoozeTypeOptions}
                          snoozeStatusOptions={snoozeStatusOptions}
                          snoozeDepartmentOptions={snoozeDepartmentOptions}
                        />
                      ) : (
                        <LogUpdates
                          snoozeItem={snoozeItem}
                          prevSnoozeItem={snoozeHistory[i + 1]}
                          snoozeTypeOptions={snoozeTypeOptions}
                          snoozeStatusOptions={snoozeStatusOptions}
                          snoozeDepartmentOptions={snoozeDepartmentOptions}
                        />
                      )}
                      {snoozeItem.note && <GridRow>{snoozeItem.note}</GridRow>}
                    </GridColumn>
                  </GridRow>
                </Grid>
              </div>
            ))
          ) : (
            <p>{t("v8_no_log_history", "No log history")}</p>
          )}
        </div>
      </Form.Field>
    </Form.Group>
  );
};
